/**
 * <s-offers> filters and sorts the server-rendered offer cards on the Bijzondere aanbiedingen page.
 *
 * Each <li data-offer> carries live Shopify data rendered by Liquid: data-cats (space-separated
 * categories), data-type(-label), data-league(-label), data-club(-label), data-sizes ("|S|M|"),
 * data-condition, data-price (cents, price_min), data-discount (%), data-created (unix), data-index.
 * Filter options are built from the cards themselves, so only real values appear. The club list
 * follows the selected competition. Each product exists once in the grid, so nothing is duplicated.
 */
class SOffers extends HTMLElement {
  #ac = null;
  #state = { cat: 'all', type: '', league: '', club: '', size: '', condition: '', price: '', sort: 'featured' };

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.items = [...this.querySelectorAll('[data-offer]')];
    this.grid = this.querySelector('[data-offer-grid]');
    if (!this.grid) return;

    this.querySelectorAll('[data-offer-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        this.#state.cat = tab.dataset.offerTab;
        this.querySelectorAll('[data-offer-tab]').forEach((t) => t.setAttribute('aria-pressed', String(t === tab)));
        this.#buildOptions();
        this.#apply();
      }, { signal });
    });

    this.querySelectorAll('[data-filter]').forEach((select) => {
      select.addEventListener('change', () => {
        this.#state[select.dataset.filter] = select.value;
        if (select.dataset.filter === 'league') this.#state.club = '';
        this.#buildOptions();
        this.#apply();
      }, { signal });
    });

    this.querySelector('[data-offer-sort]')?.addEventListener('change', (event) => {
      this.#state.sort = event.target.value;
      this.#apply();
    }, { signal });

    const reset = () => this.#reset();
    this.querySelector('[data-offer-reset]')?.addEventListener('click', reset, { signal });
    this.querySelector('[data-offer-reset-empty]')?.addEventListener('click', reset, { signal });

    this.#buildOptions();
    this.#apply();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #inCategory(item) {
    return this.#state.cat === 'all' || item.dataset.cats.split(' ').includes(this.#state.cat);
  }

  #matches(item, skip = '') {
    const s = this.#state;
    const d = item.dataset;
    if (!this.#inCategory(item)) return false;
    if (skip !== 'type' && s.type && d.type !== s.type) return false;
    if (skip !== 'league' && s.league && d.league !== s.league) return false;
    if (skip !== 'club' && s.club && d.club !== s.club) return false;
    if (skip !== 'size' && s.size && !d.sizes.includes(`|${s.size}|`)) return false;
    if (skip !== 'condition' && s.condition && d.condition !== s.condition) return false;
    if (skip !== 'price' && s.price) {
      const [min, max] = s.price.split('-');
      const price = Number(d.price);
      if (min && price < Number(min)) return false;
      if (max && price >= Number(max)) return false;
    }
    return true;
  }

  /** Rebuilds each select from the products that match every other active filter. */
  #buildOptions() {
    const allLabel = this.dataset.allLabel || '';
    const fields = {
      type: (d) => [[d.type, d.typeLabel]],
      league: (d) => [[d.league, d.leagueLabel]],
      club: (d) => [[d.club, d.clubLabel]],
      size: (d) => d.sizes.split('|').filter(Boolean).map((v) => [v, v]),
      condition: (d) => [[d.condition, d.condition]],
    };
    const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', 'XXXL', '3XL', '4XL'];

    for (const [field, extract] of Object.entries(fields)) {
      const select = this.querySelector(`[data-filter="${field}"]`);
      if (!select) continue;
      const options = new Map();
      for (const item of this.items) {
        if (!this.#matches(item, field)) continue;
        for (const [value, label] of extract(item.dataset)) {
          if (value) options.set(value, label || value);
        }
      }
      let entries = [...options.entries()];
      if (field === 'size') {
        entries.sort((a, b) => {
          const ia = sizeOrder.indexOf(a[0].toUpperCase());
          const ib = sizeOrder.indexOf(b[0].toUpperCase());
          return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a[0].localeCompare(b[0]);
        });
      } else {
        entries.sort((a, b) => a[1].localeCompare(b[1], 'nl'));
      }
      // Keep an active value selectable even if it no longer matches, so the visitor can see and clear it.
      if (this.#state[field] && !options.has(this.#state[field])) this.#state[field] = '';

      select.replaceChildren(new Option(allLabel, ''), ...entries.map(([value, label]) => new Option(label, value)));
      select.value = this.#state[field];
      // Hide a filter when no product has a value for it (for example condition before it is filled in).
      const wrap = this.querySelector(`[data-filter-wrap="${field}"]`);
      if (wrap) wrap.hidden = entries.length === 0 && !this.#state[field];
    }
  }

  #apply() {
    const s = this.#state;
    const visible = this.items.filter((item) => this.#matches(item));
    const sorters = {
      featured: (a, b) => a.dataset.index - b.dataset.index,
      'price-asc': (a, b) => a.dataset.price - b.dataset.price || a.dataset.index - b.dataset.index,
      'price-desc': (a, b) => b.dataset.price - a.dataset.price || a.dataset.index - b.dataset.index,
      newest: (a, b) => b.dataset.created - a.dataset.created || a.dataset.index - b.dataset.index,
      discount: (a, b) => b.dataset.discount - a.dataset.discount || a.dataset.index - b.dataset.index,
    };
    const ordered = [...this.items].sort(sorters[s.sort] || sorters.featured);
    const visibleSet = new Set(visible);
    for (const item of ordered) {
      item.hidden = !visibleSet.has(item);
      this.grid.append(item);
    }

    const count = visible.length;
    const countEl = this.querySelector('[data-offer-count]');
    if (countEl) {
      countEl.textContent = count === 1 ? this.dataset.countOne : (this.dataset.countOther || '').replace('__N__', String(count));
    }
    const empty = this.querySelector('[data-offer-empty]');
    if (empty) empty.hidden = count > 0;
    this.grid.hidden = count === 0;

    const filtersActive = ['type', 'league', 'club', 'size', 'condition', 'price'].some((key) => s[key]);
    const reset = this.querySelector('[data-offer-reset]');
    if (reset) reset.hidden = !filtersActive;
  }

  #reset() {
    Object.assign(this.#state, { type: '', league: '', club: '', size: '', condition: '', price: '' });
    this.#buildOptions();
    this.#apply();
  }
}

if (!customElements.get('s-offers')) customElements.define('s-offers', SOffers);
